import { useState, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  Box,
  Card,
  CardContent,
  TextField,
  IconButton,
  Typography,
  Button,
  ClickAwayListener,
} from '@mui/material';
import { IconLibrary } from '@act/storybook';
import { ChromePicker } from 'react-color';

interface DragItem {
  index: number;
  id: string;
  type: string;
}

interface DraggableColorItemProps {
  color: string;
  index: number;
  moveColor: (dragIndex: number, hoverIndex: number) => void;
  onColorChange: (index: number, color: string) => void;
  onRemove: (index: number) => void;
  canDelete: boolean;
  disabled: boolean;
  expandedColorIndex: number | null;
  onTogglePicker: (index: number) => void;
  onClickAway: () => void;
}

const DraggableColorItem = ({
  color,
  index,
  moveColor,
  onColorChange,
  onRemove,
  canDelete,
  disabled,
  expandedColorIndex,
  onTogglePicker,
  onClickAway,
}: DraggableColorItemProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: string | symbol | null }
  >({
    accept: 'color',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveColor(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'color',
    item: () => {
      return { id: `color-${index}`, index };
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !disabled,
  });

  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));

  return (
    <Box ref={ref} data-handler-id={handlerId} sx={{ mb: 1, opacity }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          backgroundColor: '#fff',
        }}
      >
        {/* Drag Handle */}
        <Box
          sx={{
            cursor: disabled ? 'not-allowed' : 'grab',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconLibrary iconName="DotsSixVertical" size={24} weight="bold" />
        </Box>

        {/* Color Preview Box */}
        <Box
          onClick={() => !disabled && onTogglePicker(index)}
          sx={{
            width: 40,
            height: 40,
            backgroundColor: color,
            borderRadius: 1,
            border: '1px solid #ccc',
            cursor: disabled ? 'not-allowed' : 'pointer',
            flexShrink: 0,
          }}
        />

        {/* Hex Input */}
        <TextField
          value={color}
          onChange={e => onColorChange(index, e.target.value)}
          disabled={disabled}
          size="small"
          fullWidth
          placeholder="#000000"
          inputProps={{
            style: { fontFamily: 'monospace' },
          }}
        />

        {/* Delete Button */}
        <IconButton
          onClick={() => onRemove(index)}
          disabled={disabled || !canDelete}
          size="small"
          color="error"
        >
          <IconLibrary iconName="Trash" size={20} />
        </IconButton>
      </Box>

      {/* Color Picker */}
      {expandedColorIndex === index && !disabled && (
        <ClickAwayListener onClickAway={onClickAway}>
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <ChromePicker
              color={color}
              onChange={colorResult => onColorChange(index, colorResult.hex)}
              disableAlpha
            />
          </Box>
        </ClickAwayListener>
      )}
    </Box>
  );
};

interface GradientEditorProps {
  label: string;
  colors: string[];
  onChange: (colors: string[]) => void;
  onCopyFrom?: () => void;
  disabled?: boolean;
}

export const GradientEditor = ({
  label,
  colors,
  onChange,
  onCopyFrom,
  disabled = false,
}: GradientEditorProps) => {
  const [expandedColorIndex, setExpandedColorIndex] = useState<number | null>(
    null
  );
  const [deletedColor, setDeletedColor] = useState<{
    color: string;
    index: number;
  } | null>(null);

  const handleAddColor = () => {
    setDeletedColor(null); // Clear undo when adding a new color
    onChange([...colors, '#000000']);
  };

  const handleRemoveColor = (index: number) => {
    // Save the deleted color and its position for undo
    setDeletedColor({ color: colors[index], index });

    const newColors = colors.filter((_, i) => i !== index);
    onChange(newColors);
    if (expandedColorIndex === index) {
      setExpandedColorIndex(null);
    }
  };

  const handleUndoDelete = () => {
    if (deletedColor) {
      const newColors = [...colors];
      // Re-insert the color at its original position
      newColors.splice(deletedColor.index, 0, deletedColor.color);
      onChange(newColors);
      setDeletedColor(null);
    }
  };

  const handleColorChange = (index: number, color: string) => {
    setDeletedColor(null); // Clear undo when changing a color
    const newColors = [...colors];
    newColors[index] = color;
    onChange(newColors);
  };

  const moveColor = (dragIndex: number, hoverIndex: number) => {
    setDeletedColor(null); // Clear undo when reordering
    const newColors = [...colors];
    const [draggedColor] = newColors.splice(dragIndex, 1);
    newColors.splice(hoverIndex, 0, draggedColor);
    onChange(newColors);
  };

  const toggleColorPicker = (index: number) => {
    setExpandedColorIndex(expandedColorIndex === index ? null : index);
  };

  const handleClickAway = () => {
    setExpandedColorIndex(null);
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">{label}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onCopyFrom && (
              <Button
                variant="outlined"
                size="small"
                onClick={onCopyFrom}
                disabled={disabled}
                startIcon={<IconLibrary iconName="Copy" size={16} />}
              >
                Copy
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={handleAddColor}
              disabled={disabled}
              startIcon={
                <IconLibrary iconName="Plus" size={16} weight="regular" />
              }
            >
              Add Color
            </Button>
          </Box>
        </Box>

        {/* Gradient Preview */}
        <Box
          sx={{
            width: '100%',
            height: 60,
            background:
              colors.length > 0
                ? `linear-gradient(135deg, ${colors.join(', ')})`
                : '#f0f0f0',
            borderRadius: 1,
            border: '1px solid #e0e0e0',
            mb: 2,
          }}
        />

        {/* Undo Delete Button */}
        {deletedColor && !disabled && (
          <Box sx={{ mb: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={handleUndoDelete}
              sx={{ textTransform: 'none' }}
            >
              Undo Delete
            </Button>
          </Box>
        )}

        {/* Color List */}
        <Box>
          {colors.map((color, index) => (
            <DraggableColorItem
              key={`color-${index}`}
              color={color}
              index={index}
              moveColor={moveColor}
              onColorChange={handleColorChange}
              onRemove={handleRemoveColor}
              canDelete={colors.length > 1}
              disabled={disabled}
              expandedColorIndex={expandedColorIndex}
              onTogglePicker={toggleColorPicker}
              onClickAway={handleClickAway}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};
